import { Question, DisplayCondition } from '../types';

/**
 * 判断问题是否应该显示
 */
export const shouldDisplayQuestion = (
  question: Question,
  answers: Record<number, any>
): boolean => {
  // 如果没有显示逻辑，始终显示
  if (!question.displayLogic) {
    return true;
  }

  const { operator, conditions } = question.displayLogic;

  // 评估所有条件
  const results = conditions.map(condition =>
    evaluateCondition(condition, answers, question)
  );

  // 根据操作符组合结果
  return operator === 'AND'
    ? results.every(r => r)  // 所有条件都满足
    : results.some(r => r);   // 任意条件满足
};

/**
 * 评估单个条件
 */
const evaluateCondition = (
  condition: DisplayCondition,
  answers: Record<number, any>,
  targetQuestion: Question
): boolean => {
  const answer = answers[condition.sourceQuestionId];

  // 如果没有答案，条件不满足
  if (answer === undefined || answer === null || answer === '') {
    return false;
  }

  const { conditionType, value } = condition;

  try {
    switch (conditionType) {
      // 单选题条件
      case 'OPTION_SELECTED':
        return answer === parseInt(value || '0');

      case 'OPTION_NOT_SELECTED':
        return answer !== parseInt(value || '0');

      // 多选题条件
      case 'OPTIONS_CONTAINS_ANY':
        const anyOptions = JSON.parse(value || '[]');
        return Array.isArray(answer) && anyOptions.some((opt: number) => answer.includes(opt));

      case 'OPTIONS_CONTAINS_ALL':
        const allOptions = JSON.parse(value || '[]');
        return Array.isArray(answer) && allOptions.every((opt: number) => answer.includes(opt));

      case 'OPTIONS_NOT_CONTAINS':
        const notOption = parseInt(value || '0');
        return Array.isArray(answer) && !answer.includes(notOption);

      // 文本题条件
      case 'TEXT_EQUALS':
        return String(answer).trim() === (value || '').trim();

      case 'TEXT_CONTAINS':
        return String(answer).includes(value || '');

      case 'TEXT_NOT_EMPTY':
        return String(answer).trim().length > 0;

      case 'TEXT_REGEX_MATCH':
        const regex = new RegExp(value || '');
        return regex.test(String(answer));

      // 滑块题条件
      case 'SLIDER_EQUALS':
        return Number(answer) === Number(value);

      case 'SLIDER_GREATER_THAN':
        return Number(answer) > Number(value);

      case 'SLIDER_LESS_THAN':
        return Number(answer) < Number(value);

      case 'SLIDER_RANGE':
        const range = JSON.parse(value || '{}');
        const numAnswer = Number(answer);
        return numAnswer >= range.min && numAnswer <= range.max;

      // 矩阵题条件
      case 'MATRIX_CELL_SELECTED':
        const cell = JSON.parse(value || '{}');
        return answer[cell.row] === cell.column;

      case 'MATRIX_ROW_SELECTED':
        const rowCol = JSON.parse(value || '{}');
        return answer[rowCol.row] === rowCol.column;

      case 'MATRIX_SCORE_RANGE':
        return evaluateMatrixScore(answer, value || '{}', targetQuestion);

      case 'MATRIX_ALL_ANSWERED':
        return evaluateMatrixAllAnswered(answer, targetQuestion);

      case 'MATRIX_ANY_ANSWERED':
        return Object.keys(answer).length > 0;

      default:
        console.warn('Unknown condition type:', conditionType);
        return false;
    }
  } catch (error) {
    console.error('Error evaluating condition:', error, condition);
    return false;
  }
};

/**
 * 评估矩阵题分数范围条件
 */
const evaluateMatrixScore = (
  answer: Record<string, string>,
  valueStr: string,
  targetQuestion: Question
): boolean => {
  try {
    const scoreCondition = JSON.parse(valueStr);
    const { row, minScore, maxScore } = scoreCondition;

    // 获取矩阵题的分数配置
    const settings = targetQuestion.settings ? JSON.parse(targetQuestion.settings) : {};
    const scores = settings.scores || {};

    // 如果指定了行，只计算该行的分数
    if (row) {
      const selectedColumn = answer[row];
      if (!selectedColumn || !scores[row]) {
        return false;
      }
      const score = scores[row][selectedColumn] || 0;
      return score >= (minScore || 0) && score <= (maxScore || 100);
    }

    // 否则计算所有已回答行的总分
    let totalScore = 0;
    for (const answeredRow in answer) {
      const selectedColumn = answer[answeredRow];
      if (scores[answeredRow] && scores[answeredRow][selectedColumn] !== undefined) {
        totalScore += scores[answeredRow][selectedColumn];
      }
    }

    return totalScore >= (minScore || 0) && totalScore <= (maxScore || 100);
  } catch (error) {
    console.error('Error evaluating matrix score:', error);
    return false;
  }
};

/**
 * 评估矩阵题是否所有行都已回答
 */
const evaluateMatrixAllAnswered = (
  answer: Record<string, string>,
  targetQuestion: Question
): boolean => {
  try {
    const settings = targetQuestion.settings ? JSON.parse(targetQuestion.settings) : {};
    const rows = settings.rows || [];

    if (rows.length === 0) {
      return false;
    }

    // 检查所有行是否都有答案
    return rows.every((row: string) => answer[row] && answer[row].trim() !== '');
  } catch (error) {
    console.error('Error evaluating matrix all answered:', error);
    return false;
  }
};
